import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authService } from '@services/api/authService';
import type { StaffUser } from '@/types/staff';
import type { Session } from '@supabase/supabase-js';

interface AuthState {
  // State
  user: StaffUser | null;
  session: Session | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;

  // Actions
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  register: (data: {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    phone_number?: string;
  }) => Promise<{ success: boolean; message?: string }>;
  checkSession: () => Promise<void>;
  clearError: () => void;
  setUser: (user: StaffUser | null) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, _get) => ({
      // Initial state
      user: null,
      session: null,
      loading: false,
      error: null,
      isAuthenticated: false,

      // Login action
      login: async (email: string, password: string) => {
        set({ loading: true, error: null });
        
        const result = await authService.login({ email, password });

        if (result.success && result.data) {
          set({
            user: result.data.staffUser,
            session: result.data.session,
            isAuthenticated: true,
            loading: false,
          });
          return true;
        } else {
          set({
            error: result.error || 'Login failed',
            loading: false,
            isAuthenticated: false,
          });
          return false;
        }
      },

      // Logout action
      logout: async () => {
        set({ loading: true });
        
        await authService.logout();
        
        set({
          user: null,
          session: null,
          isAuthenticated: false,
          loading: false,
          error: null,
        });
      },

      // Register action
      register: async (data) => {
        set({ loading: true, error: null });
        
        const result = await authService.register(data);

        if (result.success) {
          set({ loading: false });
          return {
            success: true,
            message: result.data?.message || 'Registration successful',
          };
        } else {
          set({
            error: result.error || 'Registration failed',
            loading: false,
          });
          return { success: false, message: result.error };
        }
      },

      // Check session (on app load)
      checkSession: async () => {
        set({ loading: true });
        
        const sessionResult = await authService.getSession();

        if (!sessionResult.success || !sessionResult.data) {
          set({ loading: false, isAuthenticated: false });
          return;
        }

        const profileResult = await authService.getProfile();

        if (profileResult.success && profileResult.data) {
          set({
            user: profileResult.data,
            session: sessionResult.data,
            isAuthenticated: true,
            loading: false,
          });
        } else {
          set({ loading: false, isAuthenticated: false });
        }
      },

      // Clear error
      clearError: () => set({ error: null }),

      // Set user (for updates)
      setUser: (user) => set({ user }),
    }),
    {
      name: 'mal-ops-auth',
      partialize: (state) => ({
        // Only persist user and session
        user: state.user,
        session: state.session,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

